import { useMemo } from "react";
import type { Document } from "../types";
import { usePodcasts } from "./usePodcasts";
import { useSavedPodcastsCache } from "./useSavedPodcastsCache";

export const useSavedPodcasts = () => {
	const { podcasts, isLoading } = usePodcasts();

	const { isPodcastSaved, toggleSavedPodcast } = useSavedPodcastsCache();

	const savedPodcasts = useMemo(() => {
		return podcasts.filter((podcast) =>
			isPodcastSaved(podcast.originalId ?? podcast.id),
		);
	}, [podcasts, isPodcastSaved]);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const savedPodcastsByType = useMemo(() => {
		const grouped = new Map<string, Document[]>();
		const seenPodcasts = new Set<number>();

		for (const podcast of savedPodcasts) {
			const uniqueId = podcast.originalId ?? podcast.id;
			if (seenPodcasts.has(uniqueId)) {
				continue;
			}
			seenPodcasts.add(uniqueId);

			const topics = podcast.metadata.topics;
			if (Array.isArray(topics) && topics.length > 0) {
				const firstTopic = topics[0];
				if (!grouped.has(firstTopic)) {
					grouped.set(firstTopic, []);
				}
				grouped.get(firstTopic)!.push(podcast);
			} else {
				if (!grouped.has("Uncategorized")) {
					grouped.set("Uncategorized", []);
				}
				grouped.get("Uncategorized")!.push(podcast);
			}
		}

		return Array.from(grouped)
			.map(([topic, podcasts]) => ({
				type: topic,
				podcasts,
			}))
			.filter(({ podcasts }) => podcasts.length > 0);
	}, [savedPodcasts]);

	return {
		savedPodcasts,
		savedPodcastsByType,
		isPodcastSaved,
		toggleSavedPodcast,
		isLoading,
	};
};
